import React, { memo, useEffect, useState } from 'react'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import {
  Autosuggest,
  getAutosuggestOptions,
  IAutosuggestWrapperProps,
} from 'transfix-ui/components/Autosuggest'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useContactApi } from 'apps/fleet-planner/hooks/useContactApi'
import * as Types from 'apps/fleet-planner/types'
import { getEntityById } from 'utils/getEntityById'

interface IContactAutosuggestProps extends IAutosuggestWrapperProps<Types.ICustomerContact> {
  autosuggestKey: number
  onInputChange: (val?: string) => void
}

export const ContactAutosuggest = memo<IContactAutosuggestProps>(
  ({ autosuggestKey, error, onChange, onInputChange, placeholder = 'Enter an email address' }) => {
    const { isMobile } = useBreakpointContext()
    const [inputValue, setInputValue] = useState<string>('')
    const { getContacts, contacts = [], error: apiError } = useContactApi()
    const autosuggestOptions = getAutosuggestOptions({
      options: contacts,
      groupIndex: 1,
      iconName: 'UserIcon',
      inputValue,
      mapOption: ({ email, id = 0, name = '' }: Types.ICustomerContact) => {
        return {
          id: `${id}`,
          text: `${name}`,
          email,
        }
      },
    })

    // Get contacts on mount
    useEffect(() => {
      getContacts(inputValue)
    }, [])

    const inputChangeHandler = (val: string = '') => {
      setInputValue(val)
      onInputChange(val)
      getContacts(val)
    }

    const onChangeHandler = (option?: IAutocompleteOption) => {
      const { id } = option || {}
      const newSubscriber = getEntityById(contacts, id)
      if (newSubscriber) {
        onChange(newSubscriber)
      }
    }

    return (
      <div className='fs-mask'>
        <Autosuggest
          key={autosuggestKey}
          placeholder={placeholder}
          value={inputValue}
          options={autosuggestOptions}
          onChange={onChangeHandler}
          onInputChange={inputChangeHandler}
          dataTest='contact-autosuggest'
          error={error || apiError}
          renderOptionType='withEmail'
          selectOnFocus={!isMobile}
          filterOptionsType='fuzzy'
        />
      </div>
    )
  }
)
